import { z } from 'zod';
import { ExpressionError, compile } from '@casa/layout';

// The building blocks of the dashboard schema that tile types share: names, colours, number
// formats, transforms, and the marks the editor reads from an option (its title, description and
// hint, and whether it names one of the tile's data sources). How to change the schema:
// docs/dashboard-schema.md.

/** Names of connections and data sources: letters, digits, `_` and `-`. */
export const NameSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'letters, digits, _ and - only, at most 64');

/** A colour as the legacy `CasaColor` accepted it: `#rgb`, `#rrggbb` or `#aarrggbb`. */
export const ColorSchema = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'a colour like #ffcc00');

/** A .NET custom numeric format as the legacy app used them (`0`, `0.0`, `0.##`, `#,##0`). */
export const NumberFormatSchema = z.string().regex(/^[#0,]*0(?:\.[0#]+)?$/, 'a format like 0, 0.0, 0.## or #,##0');

/** A named gradient (`red-green`, `blue-yellow-red`, …) or a list of colours. */
export const ColorsSchema = z.union([z.string().regex(/^[a-z]+(?:-[a-z]+)+$/), z.array(ColorSchema).min(2)]);

/**
 * The name a tile's short `dataSource` takes, and what `options.source` means when it is left out.
 * Most tiles read one source; they need neither a name nor a reference for it.
 */
export const PRIMARY_SOURCE = 'primary';

/** A reference to one of the tile's data sources; `primary` when left out. */
export const SourceRefSchema = NameSchema.default(PRIMARY_SOURCE);

/**
 * What a slot needs from its data (dashboard-editor spec M-4, T-10), in the kinds of value drivers
 * say they give (`@casa/driver-sdk`'s `ValueKind`): the editor offers first what fits. `commands`:
 * the slot sends commands, so what matters is what the device takes, not what it gives.
 */
export interface SlotNeeds {
    readonly gives: readonly ('number' | 'onOff' | 'level' | 'text' | 'history' | 'record' | 'forecast' | 'image' | 'video' | 'light' | 'thermostat')[];
    readonly commands?: boolean;
    /** How the editor names the slot: "Device", "Reading". */
    readonly label: string;
    /** What an optional slot left empty means, for the editor: "The value's" (a sparkline's source). */
    readonly whenUnset?: string;
}

/** A hint for the editor's form, where a text needs more than a text field (spec §5.7). */
export type Hint = 'icon' | 'color' | 'colors' | 'number-format' | 'transform' | 'condition' | 'tile-style' | 'command' | 'command-argument' | 'mode-command' | 'command-name' | 'table-rows' | 'gauge-colours';

/** What the editor shows for an option: a title, what it is for, and a hint (zod's `meta`, which replaces, so all at once). */
export function about(title: string, description?: string, hint?: Hint) : Record<string, unknown> {
    return { title, ...(description ? { description } : {}), ...(hint ? { 'x-hint': hint } : {}) };
}

/**
 * Marks an option that changes only how a tile looks, never what it shows (tile-styles.md TY-2): a
 * style picked for a tile sets these and nothing else of its options.
 */
export const LOOK = { 'x-look': true } as const;

/**
 * Marks an optional part of a tile, such as a value tile's gauge, that a style may add or take
 * away (tile-styles.md TY-2). A part a style adds starts as `start`, besides its look options; a
 * style may give those keys itself (a gauge's range).
 */
export const part = (start: Record<string, unknown>) : { readonly 'x-part': Record<string, unknown> } => ({ 'x-part': start });

/** An option that names one of the tile's sources (a slot, T-5), with what it needs. */
export function sourceOf<T extends z.ZodType>(schema: T, needs: SlotNeeds, description?: string) : T {
    return schema.meta({ ...about(needs.label, description), slot: needs });
}

export const icon = (title: string, fallback: string) => z.string().default(fallback).meta(about(title, 'by its name in the icon set', 'icon'));
export const numberFormat = (fallback: string) => NumberFormatSchema.default(fallback).meta(about('Number format', '0, 0.0, 0.## or #,##0', 'number-format'));

/**
 * An arithmetic expression on the source's value, as the legacy `transform`: `value/1000`,
 * `round(value*10)/10`, `ftoc(value)`. The constraint layout's expression language
 * (`@casa/layout`): numbers, `+ - * / %`, parentheses and functions such as `min`, `round`,
 * `ftoc`; the one name is `value`. No code runs. Converting units is done here, not by the unit,
 * which is only text (tiles spec TU-1 to TU-3).
 */
export const TransformSchema = z.string().superRefine((source, context) => {
    try {
        if (typeof compile(source).evaluate({ value: 1 }) !== 'number') {
            context.addIssue({ code: 'custom', message: 'a transform may only use the name "value"' });
        }
    } catch (error) {
        context.addIssue({ code: 'custom', message: error instanceof ExpressionError ? error.message : String(error) });
    }
});

/**
 * A condition on the value (dashboard-editor spec §5.10 SR-4): the transform's language with
 * comparisons and `and`, `or`, `not`, such as `value > 10` or `value >= 5 and value < 10`. It holds
 * when it gives a number that is not 0; the one name is `value`.
 */
export const ConditionSchema = z.string().min(1).superRefine((source, context) => {
    try {
        if (typeof compile(source).evaluate({ value: 1 }) !== 'number') {
            context.addIssue({ code: 'custom', message: 'a condition may only use the name "value"' });
        }
    } catch (error) {
        context.addIssue({ code: 'custom', message: error instanceof ExpressionError ? error.message : String(error) });
    }
});

export const transform = () => TransformSchema.optional().meta(about('Transform', 'arithmetic on the value, such as value/1000 or ftoc(value)', 'transform'));
